import { Suspense, lazy, useEffect, useState } from "react";
import { Route, Switch, useLocation } from "wouter";
import { Header }            from "@/components/Header";
import { Footer }            from "@/components/Footer";
import { CommandPalette }    from "@/components/CommandPalette";
import { ChatDock }          from "@/components/ChatDock";
import { SchemeDetailModal } from "@/components/SchemeDetailModal";
import { HomePage }          from "@/pages/Home";

/**
 * Sub-routes are code-split — the home bundle stays lean (~80KB gz)
 * and secondary pages load on demand.
 */
const PersonaPage  = lazy(() => import("@/pages/Persona").then((m)  => ({ default: m.PersonaPage })));
const MinistryPage = lazy(() => import("@/pages/Ministry").then((m) => ({ default: m.MinistryPage })));
const StatePage    = lazy(() => import("@/pages/State").then((m)    => ({ default: m.StatePage })));
const SchemePage   = lazy(() => import("@/pages/Scheme").then((m)   => ({ default: m.SchemePage })));
const OraclePage   = lazy(() => import("@/pages/Oracle").then((m)   => ({ default: m.OraclePage })));

function RouteLoading() {
  return (
    <main className="max-w-4xl mx-auto px-5 py-20 flex items-center gap-3 text-cream-100/55 font-mono text-[11px] uppercase tracking-[0.3em]">
      <span className="loader" /> reading vārtā…
    </main>
  );
}

export default function App() {
  const [location, navigate]          = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [chatOpen, setChatOpen]       = useState(false);
  const [modalId, setModalId]         = useState<string | null>(null);
  const [pendingPrompt, setPending]   = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    const onOpenChat     = () => setChatOpen(true);
    const onOpenPalette  = () => setPaletteOpen(true);
    const onOpenScheme   = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (id) setModalId(id);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("indstartups:open-chat",    onOpenChat);
    window.addEventListener("indstartups:open-palette", onOpenPalette);
    window.addEventListener("indstartups:open-scheme",  onOpenScheme as EventListener);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("indstartups:open-chat",    onOpenChat);
      window.removeEventListener("indstartups:open-palette", onOpenPalette);
      window.removeEventListener("indstartups:open-scheme",  onOpenScheme as EventListener);
    };
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [location]);

  // Per-route document.title for SEO + shareability.
  useEffect(() => { applyTitle(location); }, [location]);

  const askOracle = (prompt: string) => { setPending(prompt); setChatOpen(true); };

  return (
    <>
      <Header />

      <Switch>
        <Route path="/">
          <HomePage
            onOpenScheme={setModalId}
            onAskAi={askOracle}
            onOpenPalette={() => setPaletteOpen(true)}
          />
        </Route>
        <Route path="/persona/:slug">
          {(params) => (
            <Suspense fallback={<RouteLoading />}>
              <PersonaPage slug={params.slug} />
            </Suspense>
          )}
        </Route>
        <Route path="/ministry/:slug">
          {(params) => (
            <Suspense fallback={<RouteLoading />}>
              <MinistryPage slug={params.slug} />
            </Suspense>
          )}
        </Route>
        <Route path="/state/:slug">
          {(params) => (
            <Suspense fallback={<RouteLoading />}>
              <StatePage slug={params.slug} />
            </Suspense>
          )}
        </Route>
        <Route path="/scheme/:id">
          {(params) => (
            <Suspense fallback={<RouteLoading />}>
              <SchemePage id={params.id} />
            </Suspense>
          )}
        </Route>
        <Route path="/oracle">
          <Suspense fallback={<RouteLoading />}>
            <OraclePage />
          </Suspense>
        </Route>
        <Route>
          <main className="max-w-4xl mx-auto px-5 py-24 text-center">
            <p className="font-display text-3xl text-cream-50">Off the atlas.</p>
            <p className="mt-3 text-cream-100/60">No vārtā filed at <code>{location}</code>.</p>
            <a href="/" className="inline-block mt-6 text-amber-300 hover:underline">← back to the atlas</a>
          </main>
        </Route>
      </Switch>

      <Footer />

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onOpenScheme={(id) => setModalId(id)}
        onAskAi={askOracle}
        onNavigate={(href) => navigate(href)}
      />

      <SchemeDetailModal
        schemeId={modalId}
        onClose={() => setModalId(null)}
        onAskAi={askOracle}
      />

      <ChatDock
        open={chatOpen}
        onOpen={() => setChatOpen(true)}
        onClose={() => setChatOpen(false)}
        pendingPrompt={pendingPrompt}
        onPromptConsumed={() => setPending(null)}
        onOpenScheme={(id) => { setModalId(id); setChatOpen(false); }}
      />
    </>
  );
}

/** Per-route document.title — helps SEO crawlers that do render. */
function applyTitle(path: string) {
  const brand = "IndStartups — AI Sarva";
  let title = `IndStartups — The Founder's Atlas of India · AI Sarva`;
  let description = "An independent atlas of every Indian govt scheme, incubator, VC fund and startup community.";
  if (path.startsWith("/persona/")) {
    const slug = path.split("/")[2] || "";
    title = `${toTitle(slug)} — Founder archetype · ${brand}`;
    description = `Curated scheme + incubator stack for ${toTitle(slug)}. Hand-indexed. Free.`;
  } else if (path.startsWith("/ministry/")) {
    const slug = path.split("/")[2] || "";
    title = `Ministry · ${slug.toUpperCase()} hall · ${brand}`;
    description = `Every scheme filed under the ${slug.toUpperCase()} ministry.`;
  } else if (path.startsWith("/state/")) {
    const slug = path.split("/")[2] || "";
    title = `${toTitle(slug)} · State ecosystem · ${brand}`;
    description = `Startup mission, incubators, accelerators and schemes filed for ${toTitle(slug)}.`;
  } else if (path.startsWith("/scheme/")) {
    title = `Scheme vārtā · ${brand}`;
    description = `Full scheme details — eligibility, benefits, how to apply.`;
  } else if (path === "/oracle") {
    title = `Ākāśavāṇī · ${brand}`;
    description = `Ask in plain English. The oracle answers with cited scheme sources.`;
  }
  document.title = title;
  const meta = document.querySelector("meta[name=description]");
  if (meta) meta.setAttribute("content", description);
  const og = document.querySelector("meta[property='og:title']");
  if (og) og.setAttribute("content", title);
  const ogd = document.querySelector("meta[property='og:description']");
  if (ogd) ogd.setAttribute("content", description);
  const canonical = document.querySelector("link[rel=canonical]");
  if (canonical) canonical.setAttribute("href", `https://ind-startups.aisarva.com${path}`);
}

function toTitle(slug: string): string {
  return slug.split("-").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ");
}
